import React, { useState, useEffect, useRef, ReactNode } from 'react';
import { FaJava, FaHtml5, FaCss3Alt, FaReact, FaAngular, FaDocker, FaGitAlt, FaLinux, FaAws, FaTerminal } from 'react-icons/fa';
import patternflyLogo from '../patternfly.png';
import { SiC, SiCplusplus, SiJavascript, SiKubernetes, SiDjango, SiMongodb, SiPowerapps, SiPowerautomate, SiTypescript, SiTensorflow, SiKeras, SiMysql, SiOracle, SiDataverse, SiPython, SiAnsible, SiRedhatopenshift, SiPodman, SiBackstage, SiPostgresql, SiRedhat, SiGo, SiGooglecloud, SiTerraform, SiGithubactions, SiTailwindcss, SiBootstrap, SiFlask } from 'react-icons/si';
import './Skill.css';

interface Skill {
  name: string;
  icon: ReactNode;
}

const Skills: React.FC = () => {
  const [flippedIndex, setFlippedIndex] = useState<number | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const skillsRef = useRef<HTMLDivElement | null>(null);

  const skills: Skill[] = [
    // Research (IIIT Bangalore)
    { name: 'Python', icon: <SiPython color="#3776AB" /> },
    { name: 'TensorFlow', icon: <SiTensorflow color="#FF6F00" /> },
    { name: 'Keras', icon: <SiKeras color="#D00000" /> },
    
    // Red Hat
    { name: 'Ansible', icon: <SiAnsible color="#EE0000" /> },
    { name: 'RHEL', icon: <SiRedhat color="#EE0000" /> },
    { name: 'OpenShift', icon: <SiRedhatopenshift color="#EE0000" /> },
    { name: 'Terraform', icon: <SiTerraform color="#7B42BC" /> },
    { name: 'Go', icon: <SiGo color="#00ADD8" /> },
    { name: 'Podman', icon: <SiPodman color="#892CA0" /> },
    { name: 'Backstage', icon: <SiBackstage color="#9BF0E1" /> },
    { name: 'PatternFly', icon: <img src={patternflyLogo} alt="PatternFly" style={{ width: '2rem', height: '2rem' }} /> },
    { name: 'AWS', icon: <FaAws color="#FF9900" /> },
    { name: 'GCP', icon: <SiGooglecloud color="#4285F4" /> },
    { name: 'GitHub Actions', icon: <SiGithubactions color="#2088FF" /> },
    { name: 'Shell Scripting', icon: <FaTerminal color="#4EAA25" /> },
    { name: 'PostgreSQL', icon: <SiPostgresql color="#4169E1" /> },
    
    // College
    { name: 'C', icon: <SiC color="#A8B9CC" /> },
    { name: 'C++', icon: <SiCplusplus color="#00599C" /> },
    { name: 'Java', icon: <FaJava color="#007396" /> },
    { name: 'HTML', icon: <FaHtml5 color="#E34F26" /> },
    { name: 'CSS', icon: <FaCss3Alt color="#1572B6" /> },
    { name: 'JavaScript', icon: <SiJavascript color="#F7DF1E" /> },
    { name: 'Tailwind', icon: <SiTailwindcss color="#06B6D4" /> },
    { name: 'Bootstrap', icon: <SiBootstrap color="#7952B3" /> },
    { name: 'MySQL', icon: <SiMysql color="#4479A1" /> },
    { name: 'Oracle', icon: <SiOracle color="#F80F0F" /> },
    { name: 'Git', icon: <FaGitAlt color="#F05032" /> },
    { name: 'Linux', icon: <FaLinux color="#FCC624" /> },
    
    // PharmEasy
    { name: 'React', icon: <FaReact color="#61DAFB" /> },
    { name: 'TypeScript', icon: <SiTypescript color="#3178C6" /> },
    { name: 'Docker', icon: <FaDocker color="#2496ED" /> },
    { name: 'Kubernetes', icon: <SiKubernetes color="#326CE5" /> },
    { name: 'MongoDB', icon: <SiMongodb color="#47A248" /> },
    { name: 'Django', icon: <SiDjango color="#108207" /> },
    
    // Caterpillar
    { name: 'PowerApps', icon: <SiPowerapps color="#742774" /> },
    { name: 'PowerAutomate', icon: <SiPowerautomate color="#0066FF" /> },
    { name: 'Dataverse', icon: <SiDataverse color="#44C460" /> },
    { name: 'Angular', icon: <FaAngular color="#DD0031" /> },
    
    // Additional
    { name: 'Flask', icon: <SiFlask color="#000000" /> }
  ];

  const handleClick = (index: number) => {
    setFlippedIndex(flippedIndex === index ? null : index);
  };

  useEffect(() => {
    const node = skillsRef.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      { threshold: 0.1 }
    );

    if (node) {
      observer.observe(node);
    }

    return () => {
      if (node) {
        observer.unobserve(node);
      }
    };
  }, []);

  return (
    <div
      ref={skillsRef}
      className={`skills-container ${isVisible ? 'fade-in' : 'fade-out'}`}
    >
      <h2>Skills</h2>
      <div className="skills-grid">
        {skills.map((skill, index) => (
          <div
            key={skill.name}
            className={isVisible ? `bounce-${(index % 4) + 1}` : ''}
          >
            <div className="skill-card" onClick={() => handleClick(index)}>
              <div
                className="skill-card-inner"
                style={{
                  transform: flippedIndex === index ? 'rotateY(180deg)' : 'rotateY(0deg)',
                }}
              >
                <div className="front">
                  {skill.icon}
                </div>
                <div className="back">
                  {skill.name}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Skills;
